/**
 * PulseCIO - Frontend Application
 * Copyright (c) 2025-2026 Mathieu Lalande. All rights reserved.
 * 
 * This is proprietary software. Unauthorized copying, modification,
 * distribution, or use of this software is strictly prohibited.
 * See LICENSE file for full terms.
 */

import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext.jsx';
import { MessagesProvider } from './context/MessagesContext.jsx';
import { TutorialProvider } from './context/TutorialContext.jsx';
import TutorialOverlay from './components/tutorial/TutorialOverlay.jsx';
import { ToastProvider } from './components/toast/CollapsedToastContainer';
import CollapsedToastContainer from './components/toast/CollapsedToastContainer';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword.jsx';
import ResetPassword from './pages/ResetPassword.jsx';
import VerifyEmail from './pages/VerifyEmail.jsx';
import JoinTenant from './pages/JoinTenant.jsx';
import Messages from './pages/Messages.jsx';
import Profile from './pages/Profile.jsx';
import TenantSettings from './pages/TenantSettings.jsx';
import Notifications from './pages/Notifications.jsx';
import PaymentSuccess from './pages/PaymentSuccess.jsx';
import ManageOrganizations from './pages/ManageOrganizations.jsx';
import OAuthCallback from './pages/OAuthCallback.jsx';

// Public informational pages
import Legal from './pages/Legal.jsx';
import Privacy from './pages/Privacy.jsx';
import Terms from './pages/Terms.jsx';
import Cookies from './pages/Cookies.jsx';
import About from './pages/About.jsx';
import Status from './pages/Status.jsx';
import CookieBanner from './components/notifications/CookieBanner.jsx';
import PopUpNewVersion from './components/shared/PopUpNewVersion.jsx';
import PopUpInfo from './components/shared/PopUpInfo.jsx';
import LoadingScreen from './components/shared/LoadingScreen.jsx';
import './index.css';

function useVersionInfo() {
  const [versionInfo, setVersionInfo] = useState(null);
  useEffect(() => {
    fetch('/version.json')
      .then((res) => res.json())
      .then(setVersionInfo)
      .catch(() => setVersionInfo(null));
  }, []);
  return versionInfo;
}

function useInfo() {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    fetch('/info.json')
      .then((res) => res.json())
      .then(setInfo)
      .catch(() => setInfo(null));
  }, []);
  return info;
}

const ProtectedRoute = ({ children, requireTenant = true }) => {
  const { user, loading, activeTenantId } = useAuth();

  if (loading) {
    return <LoadingScreen appName="PulseCIO" />;
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Si la route nécessite un tenant et qu'il n'y en a pas, rediriger vers join-tenant
  if (requireTenant && !activeTenantId) {
    return <Navigate to="/join-tenant" replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading, activeTenantId } = useAuth();

  if (loading) {
    return <LoadingScreen appName="PulseCIO" />;
  }

  // Si l'utilisateur est connecté
  if (user) {
    // S'il a un tenant actif, aller à /messages
    if (activeTenantId) {
      return <Navigate to="/messages" replace />;
    }
    // Sinon, aller à /join-tenant
    return <Navigate to="/join-tenant" replace />;
  }

  return children;
};

const App = () => {

  const versionInfo = useVersionInfo();
  const info = useInfo();

  // Gestion de la popup d'information générale (annonce/maintenance/info)
  const [showInfoPopup, setShowInfoPopup] = useState(false);

  useEffect(() => {
    if (!info || info.show === false) {
      setShowInfoPopup(false);
      return;
    }
    const lastClosed = localStorage.getItem('popupInfoClosed');
    if (lastClosed === info.date + (info.type || '')) {
      setShowInfoPopup(false);
    } else {
      setShowInfoPopup(true);
    }
  }, [info]);

  const handleCloseInfoPopup = () => {
    if (info) {
      localStorage.setItem('popupInfoClosed', info.date + (info.type || ''));
    }
    setShowInfoPopup(false);
  };

  // Gestion de la popup de version (affichée après la popup info)
  const [showVersionPopup, setShowVersionPopup] = useState(() => {
    const lastClosed = localStorage.getItem('popupNewVersionClosed');
    if (versionInfo && lastClosed === versionInfo.version) return false;
    return true;
  });

  useEffect(() => {
    if (!versionInfo) return;
    const lastClosed = localStorage.getItem('popupNewVersionClosed');
    if (lastClosed === versionInfo.version) {
      setShowVersionPopup(false);
    } else {
      setShowVersionPopup(true);
    }
  }, [versionInfo]);

  const handleCloseVersionPopup = () => {
    if (versionInfo) {
      localStorage.setItem('popupNewVersionClosed', versionInfo.version);
    }
    setShowVersionPopup(false);
  };

  return (
    <ToastProvider>
      <AuthProvider>
        <MessagesProvider>
          <TutorialProvider>
          <Router>
            <Routes>
              {/* Public info routes (accessible without authentication) */}
              <Route path="/legal" element={<Legal />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/cookies" element={<Cookies />} />
              <Route path="/about" element={<About />} />
              <Route path="/status" element={<Status />} />

              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <Login />
                  </PublicRoute>
                }
              />
              <Route
                path="/forgot-password"
                element={
                  <PublicRoute>
                    <ForgotPassword />
                  </PublicRoute>
                }
              />
              <Route
                path="/reset-password"
                element={
                  <PublicRoute>
                    <ResetPassword />
                  </PublicRoute>
                }
              />
              <Route path="/verify-email" element={<VerifyEmail />} />

              {/* Route de callback OAuth */}
              <Route path="/oauth/callback" element={<OAuthCallback />} />

              {/* Canonicalize root to /login so the login page is available at /login */}
              <Route
                path="/"
                element={<Navigate to="/login" replace />}
              />
              <Route
                path="/join-tenant"
                element={
                  <ProtectedRoute requireTenant={false}>
                    <JoinTenant />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/messages"
                element={
                  <ProtectedRoute requireTenant={true}>
                    <Messages />
                  </ProtectedRoute>
                }
              />
              {/* Route de succès de paiement Stripe */}
              <Route
                path="/payment/success"
                element={
                  <ProtectedRoute requireTenant={false}>
                    <PaymentSuccess />
                  </ProtectedRoute>
                }
              />
              {/* Route de fallback pour les redirections Stripe */}
              <Route
                path="/messages/pricing"
                element={<Navigate to="/messages?canceled=1" replace />}
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute requireTenant={true}>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/notifications"
                element={
                  <ProtectedRoute requireTenant={true}>
                    <Notifications />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tenant-settings"
                element={
                  <ProtectedRoute requireTenant={true}>
                    <TenantSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manage-organizations"
                element={
                  <ProtectedRoute requireTenant={false}>
                    <ManageOrganizations />
                  </ProtectedRoute>
                }
              />
            </Routes>
            {/* Popup d'information générale (annonce/maintenance/info) */}
            {info && info.show !== false && showInfoPopup && (
              <PopUpInfo
                type={info.type}
                title={info.title}
                message={info.message}
                date={info.date}
                onClose={handleCloseInfoPopup}
                maintenanceMode={info.maintenanceMode}
              />
            )}
            {/* Popup de version (affichée après la popup info) */}
            {versionInfo && showVersionPopup && !showInfoPopup && (
              <PopUpNewVersion
                version={versionInfo.version}
                date={versionInfo.date}
                changes={versionInfo.changes}
                onClose={handleCloseVersionPopup}
              />
            )}
            <CookieBanner />
            <CollapsedToastContainer />
            <TutorialOverlay />
          </Router>
          </TutorialProvider>
        </MessagesProvider>
      </AuthProvider>
    </ToastProvider>
  );
};

export default App;